"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { FeedbackThread, ScreenshotLightbox } from "@/entities/feature-announcement-feedback";
import type { FeedbackThreadAttachment, LightboxScreenshot } from "@/entities/feature-announcement-feedback";
import type { MyFeedbackThread } from "@/presentation/rpc/feature-announcements-rpc";
import { useAnalytics, getEvent } from "@/lib/analytics";
import { useToggleFeedbackReaction } from "../model/feedback-conversations-hooks";
type ThreadFeedback = MyFeedbackThread["feedback"];
type ThreadMessage = MyFeedbackThread["messages"][number];

/**
 * The conversation, messaging-app style: the original submission opens the
 * thread as the submitter's own bubble ("You", right), team replies render
 * under the replier's first name with a "· Cova" marker, or "Cova" when the
 * server could not resolve one (left). The raw author is never on the user wire
 * (omitted via S.omit); only the server-resolved first name is.
 *
 * Thin wrapper over `@/entities/feature-announcement-feedback` `FeedbackThread`.
 */
export function FeedbackThreadView(t0) {
  const $ = _c(24);
  const {
    feedback,
    messages
  } = t0;
  const [lightbox, setLightbox] = useState(null);
  const toggleReaction = useToggleFeedbackReaction();
  const {
    track
  } = useAnalytics();
  let t1;
  if ($[0] !== feedback.comment || $[1] !== feedback.createdAt || $[2] !== feedback.id || $[3] !== feedback.reactions) {
    t1 = feedback.comment ? [{
      id: `original-${feedback.id}`,
      noteId: null,
      authorType: "user" as const,
      content: feedback.comment,
      createdAt: feedback.createdAt,
      reactions: feedback.reactions
    }] : [];
    $[0] = feedback.comment;
    $[1] = feedback.createdAt;
    $[2] = feedback.id;
    $[3] = feedback.reactions;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== messages || $[6] !== t1) {
    t2 = [...t1, ...messages.map(_temp2)];
    $[5] = messages;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const thread = t2;
  let t3;
  if ($[8] !== feedback.id || $[9] !== toggleReaction || $[10] !== track) {
    t3 = (noteId, emoji) => {
      toggleReaction.mutate({
        feedbackId: feedback.id,
        noteId,
        emoji
      }, {
        onSuccess: result => {
          track(getEvent("feature-announcement", "feedback_reaction_toggled"), {
            feedbackId: feedback.id,
            emoji,
            added: result.added
          });
        }
      });
    };
    $[8] = feedback.id;
    $[9] = toggleReaction;
    $[10] = track;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleToggleReaction = t3;
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (attachments, index) => {
      setLightbox({
        screenshots: attachments.map(_temp3),
        index
      });
    };
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const openLightbox = t4;
  let t5;
  if ($[13] !== handleToggleReaction || $[14] !== thread) {
    t5 = thread.length > 0 && <FeedbackThread perspective="submitter" messages={thread} onImageClick={openLightbox} onToggleReaction={handleToggleReaction} />;
    $[13] = handleToggleReaction;
    $[14] = thread;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== messages.length) {
    t6 = messages.length === 0 && <p className="px-1 text-xs italic text-gray-400">No replies yet. The Cova team will respond here.</p>;
    $[16] = messages.length;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== lightbox) {
    t7 = lightbox && <ScreenshotLightbox screenshots={lightbox.screenshots} currentIndex={lightbox.index} onClose={() => setLightbox(null)} onNavigate={index_0 => setLightbox(prev => prev ? {
      ...prev,
      index: index_0
    } : prev)} />;
    $[18] = lightbox;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className="space-y-3">{t5}{t6}{t7}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp3(a_0) {
  return {
    id: a_0.id,
    url: a_0.url,
    fileName: a_0.fileName
  };
}
function _temp2(m) {
  return {
    id: m.id,
    noteId: m.id,
    authorType: m.authorType,
    authorFirstName: m.authorFirstName,
    content: m.content,
    createdAt: m.createdAt,
    attachments: (m.attachments ?? []).map(_temp),
    reactions: m.reactions
  };
}
function _temp(a) {
  return {
    id: a.id,
    url: a.url,
    fileName: a.fileName
  };
}